import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  MoreHorizontal,
  ArrowLeftRight,
  Phone,
  Video,
  Star,
  FileText,
  Loader2,
  AlertCircle,
  Image,
  Paperclip,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  Search,
  ChevronDown,
  ExternalLink,
  Camera,
  FileIcon,
  ChevronLeft,
  ChevronRight,
  Mail,
  Info,
  CalendarIcon,
  Eye,
  ArrowUp,
} from "lucide-react";
import { logger } from "@/utils/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FilePreviewDialog } from "@/components/ui/file-preview";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import {
  usePingChatDetail,
  useReassignmentTargets,
} from "@/hooks/ping/usePingChats";
import { useInternalChatDetail } from "@/hooks/ping/useInternalChats";
import { usePingMessagesInfinite } from "@/hooks/ping/usePingMessages";
import { useInternalMessagesInfinite } from "@/hooks/ping/useInternalMessages";
import {
  useSendTextMessage,
  useSendImageMessage,
  useSendDocumentMessage,
  useUpdateChatStatus,
  useTransferChat,
  useMarkMessagesAsRead,
  useReassignChat,
  useStarChat,
  useMarkChatAsUnread,
} from "@/hooks/ping/usePingMutations";
import {
  useSendInternalTextMessage,
  useSendInternalImageMessage,
  useSendInternalDocumentMessage,
  useMarkInternalMessagesAsRead,
  useStarInternalChat,
  useMarkInternalChatAsUnread,
} from "@/hooks/ping/useInternalChatMutations";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { useToast } from "@/hooks/use-toast";
import { useRealtimeMessages } from "@/hooks/useRealtimeMessages";
import { AddTrackerEntryModal } from "./AddTrackerEntryModal";
import { TrackerSelectionDropdown } from "./TrackerSelectionDropdown";
import { InfoAccordionSection } from "./InfoAccordionSection";
import {
  usePatientDocuments,
  getDocumentTypeName,
  isImageDocument,
} from "@/hooks/usePatientDocuments";
import { DOCUMENT_TYPES, apiService, type DocumentType } from "@/lib/api";
import type {
  Message,
  SupportChatDetail,
  InternalChatDetail,
  InternalMessage,
  ReassignmentTarget,
} from "@/types";
import type { HealthMetric } from "@/types/tracker";
import { cn } from "@/lib/utils";
import { SiteIcon } from "@/components/icons/siteIcons";
import { iconography } from "@/design-system/tokens/iconography";

interface ConversationViewProps {
  conversationId: string;
  chatType?: "support" | "internal";
}

// Helper function to format message time
const formatMessageTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

// Helper function to format date
const formatMessageDate = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString();
  }
};

// Helper function to get user initials
const getUserInitials = (firstName?: string, lastName?: string): string => {
  if (!firstName || !lastName) {
    return "NA";
  }
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

// Helper function to get file extension
const getFileExtension = (filename: string): string => {
  // Remove query parameters and hash from URL if present
  const cleanFilename = filename.split("?")[0].split("#")[0];
  // Get the last part after splitting by '/'
  const filenamePart = cleanFilename.split("/").pop() || "";
  // Extract extension
  const extension = filenamePart.split(".").pop()?.toLowerCase() || "";
  return extension;
};

// Helper function to determine if file is an image
const isImageFile = (filename: string): boolean => {
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
  return imageExtensions.includes(getFileExtension(filename));
};

// Helper function to determine if file is a PDF
const isPdfFile = (filename: string): boolean => {
  return getFileExtension(filename) === "pdf";
};

// Helper function to get file icon based on type
const getFileIcon = (filename: string) => {
  const extension = getFileExtension(filename);

  const IconWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="size-6 flex items-center justify-center">{children}</div>
  );

  if (isImageFile(filename)) {
    return (
      <IconWrapper>
        <Camera className="w-4 h-4" />
      </IconWrapper>
    );
  }

  const CommonIcon = () => {
    switch (extension) {
      case "pdf":
      case "doc":
      case "docx":
      case "xls":
      case "xlsx":
        return <FileText className="w-4 h-4" />;
      default:
        return <FileIcon className="w-4 h-4" />;
    }
  };

  return (
    <IconWrapper>
      <CommonIcon />
    </IconWrapper>
  );
};

const getReassignmentRoleLabel = (role: string): string => {
  switch (role) {
    case "staff_admin":
      return "Admin";
    case "care_manager":
      return "Care Manager";
    case "nutritionist":
      return "Nutritionist";
    case "physiotherapist":
      return "Physiotherapist";
    default:
      return role;
  }
};

// Helper function to detect and render links in text
const renderTextWithLinks = (text: string) => {
  // URL regex pattern to match various URL formats
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;

  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      // Ensure the URL has a protocol
      const href = part.startsWith("http") ? part : `https://${part}`;

      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 underline cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            window.open(href, "_blank");
          }}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

// New MediaBox component matching Flutter design
const MediaBox = ({
  fileName,
  fileType,
  isSender,
  onTap,
}: {
  fileName: string;
  fileType: string;
  isSender: boolean;
  onTap: () => void;
}) => {
  // Check if file is an image
  const isImage = isImageFile(fileName);

  return (
    <div
      className={`flex h-[37px] w-[300px] items-center rounded-[5px] border px-2 shadow-sm ${isSender ? "bg-primary/10" : "bg-card"}`}
    >
      <div className="flex items-center flex-1 min-w-0">
        {getFileIcon(fileName)}
        <span className="z-body-md ml-2 truncate text-foreground">
          {fileName}
        </span>
      </div>
      <button
        onClick={onTap}
        className="w-7 h-7 flex items-center justify-center bg-background/50 hover:bg-muted rounded transition-colors"
      >
        <Eye className="h-4 w-4" />
      </button>
    </div>
  );
};

// Message component for better organization
const MessageItem = ({
  message,
  patientInitials,
  isCurrentUser,
  onMediaClick,
}: {
  message: Message | InternalMessage;
  patientInitials: string;
  isCurrentUser: boolean;
  onMediaClick: (
    url: string,
    type: "image" | "document",
    filename: string
  ) => void;
}) => {
  // Check if message is a note (only exists on Message type, not InternalMessage)
  const isNote = "is_note" in message && message.is_note;

  if (isNote) {
    return (
      <div className="flex justify-center w-full">
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 max-w-md min-w-0">
          <div className="flex items-start space-x-2 mb-1">
            <Badge variant="outline" className="z-eyebrow">
              Internal Note
            </Badge>
          </div>
          <p className="z-body whitespace-pre-wrap break-words overflow-wrap-anywhere text-foreground">
            {message.text_content}
          </p>
          <div className="flex justify-end mt-2">
            <span className="z-caption z-numeric text-muted-foreground">
              Note added • {message.sender_name} •{" "}
              {formatMessageTime(message.created_at)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (message.message_type === "text") {
    return (
      <div className={cn("space-y-2", isCurrentUser ? "ml-auto max-w-md" : "")}>
        {isCurrentUser ? (
          /* Current user's message (right side) */
          <>
            <div className="flex flex-col items-end">
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 max-w-sm min-w-0">
                <p className="z-body whitespace-pre-wrap break-words overflow-wrap-anywhere text-foreground">
                  {renderTextWithLinks(message.text_content || "")}
                </p>
              </div>
              <div className="mt-1">
                <span className="z-caption z-numeric block text-right text-muted-foreground">
                  {formatMessageTime(message.created_at)} •{" "}
                  {message.sender_name}
                </span>
              </div>
            </div>
          </>
        ) : (
          /* Other user's message (left side) */
          <>
            <div className="flex flex-col items-start">
              <div className="bg-muted border border-border rounded-lg p-3 max-w-sm min-w-0">
                <p className="z-body whitespace-pre-wrap break-words overflow-wrap-anywhere text-muted-foreground">
                  {renderTextWithLinks(message.text_content || "")}
                </p>
              </div>
              <div className="z-caption z-numeric mt-1 text-muted-foreground">
                <span>{message.sender_name}</span> •{" "}
                <span>{formatMessageTime(message.created_at)}</span>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Media Message (Image or Document)
  if (message.message_type === "image" || message.message_type === "document") {
    const rawFilename = message.media_file
      ? message.media_file.split("/").pop() || "File"
      : "File";
    // Strip query parameters from URL-based filenames
    const filename = rawFilename.split("?")[0].split("#")[0];
    const fileExtension = getFileExtension(filename);
    const isImage = isImageFile(filename);

    const handleMediaAction = () => {
      if (message.media_file) {
        // Open all media (images and documents) in the modal viewer
        onMediaClick(
          message.media_file,
          message.message_type as "image" | "document",
          filename
        );
      }
    };

    return (
      <div
        className={cn(
          "space-y-1 flex flex-col",
          isCurrentUser
            ? "ml-auto max-w-md items-end" // Align right for current user
            : "items-start" // Align left for other user
        )}
      >
        {/* Wrap media + text in one bubble */}
        <div
          className={cn(
            "rounded-lg p-2 max-w-sm min-w-0",
            isCurrentUser
              ? "bg-primary/10 border border-primary/20"
              : "bg-muted border border-border"
          )}
        >
          <MediaBox
            fileName={filename}
            fileType={fileExtension}
            isSender={isCurrentUser}
            onTap={handleMediaAction}
          />

          {message.text_content && (
            <p className="z-body mt-2 whitespace-pre-wrap break-words overflow-wrap-anywhere text-foreground">
              {renderTextWithLinks(message.text_content)}
            </p>
          )}
        </div>

        {/* Metadata (time + sender) */}
        <div className="z-caption z-numeric text-muted-foreground">
          {isCurrentUser ? (
            <span>
              {formatMessageTime(message.created_at)} • {message.sender_name}
            </span>
          ) : (
            <span>
              {message.sender_name} • {formatMessageTime(message.created_at)}
            </span>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export const ConversationView = ({
  conversationId,
  chatType = "support",
}: ConversationViewProps) => {
  const isInternal = chatType === "internal";
  const [message, setMessage] = useState("");
  const [note, setNote] = useState("");
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const MAX_FILES = 3;
  const [isDragOver, setIsDragOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [isTypingIndicatorVisible, setIsTypingIndicatorVisible] =
    useState(false);
  // Hide patient info by default on screens smaller than xl (1280px)
  const [showPatientInfo, setShowPatientInfo] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1280 : false
  );

  // Add view activity tracking - matches Flutter implementation
  const [isViewActive, setIsViewActive] = useState(true);
  const [isUserViewingHistory, setIsUserViewingHistory] = useState(false);

  // Track if messages are currently being loaded
  const [isLoadingNewMessage, setIsLoadingNewMessage] = useState(false);

  // Add ref to track if we're currently processing a mark-as-read
  const isMarkingAsReadRef = useRef(false);
  const lastMarkedMessageIdRef = useRef<string | null>(null);

  // Collapsible sections state
  const [isContactInfoExpanded, setIsContactInfoExpanded] = useState(false);
  const [isLastVisitsExpanded, setIsLastVisitsExpanded] = useState(false);
  const [showReadingModal, setShowReadingModal] = useState(false);
  const [selectedTracker, setSelectedTracker] = useState<HealthMetric | null>(
    null
  );

  // Document uploads state
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<DocumentType | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // File viewer calendar popover state
  const [viewerCalendarOpen, setViewerCalendarOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Document viewer dialog state
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    id: string;
    url: string;
    fileName: string;
    documentType: DocumentType | null;
    documentDate: Date | null;
    createdAt: Date | null;
    readOnly?: boolean;
  } | null>(null);
  const [viewerDocumentType, setViewerDocumentType] =
    useState<DocumentType | null>(null);
  const [viewerDocumentDate, setViewerDocumentDate] = useState<Date | null>(
    null
  );

  // Categorization dialog state
  const [isCategorizeDialogOpen, setIsCategorizeDialogOpen] = useState(false);
  const [pendingCategorizeType, setPendingCategorizeType] =
    useState<DocumentType | null>(null);
  const [isCategorizing, setIsCategorizing] = useState(false);

  // Date change confirmation dialog state
  const [isDateChangeDialogOpen, setIsDateChangeDialogOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);
  const [isDateChanging, setIsDateChanging] = useState(false);

  // Handle file download from uploads
  const handleFileDownload = (fileUrl: string, fileName: string) => {
    const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

    // For file types that typically open in browser (like PDFs), open in new tab
    const browserOpenableTypes = [
      "pdf",
      "png",
      "jpg",
      "jpeg",
      "gif",
      "svg",
      "webp",
    ];

    if (browserOpenableTypes.includes(fileExtension)) {
      // Open in new tab for viewable files
      window.open(fileUrl, "_blank");
    } else {
      // Try to download for other file types
      try {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileName;
        link.target = "_blank"; // Fallback to new tab if download fails

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Fallback: open in new tab if download fails
        window.open(fileUrl, "_blank");
      }
    }
  };

  // Handle tracker selection
  const handleTrackerSelect = (tracker: HealthMetric) => {
    setSelectedTracker(tracker);
    setShowReadingModal(true);
  };

  // Handle document viewer open
  const handleDocumentViewerOpen = (
    id: string,
    url: string,
    fileName: string,
    documentType: DocumentType | null,
    documentDate?: string | null,
    createdAt?: string,
    readOnly?: boolean
  ) => {
    setSelectedDocument({
      id,
      url,
      fileName,
      documentType,
      documentDate: documentDate ? new Date(documentDate) : null,
      createdAt: createdAt ? new Date(createdAt) : null,
      readOnly,
    });
    setViewerDocumentType(documentType);
    setViewerDocumentDate(documentDate ? new Date(documentDate) : null);
    setIsDocumentViewerOpen(true);
  };

  // Document type filter handlers
  const handleDocumentTypeSelect = (type: DocumentType) => {
    // If clicking the same type, deselect it (go back to "All")
    if (selectedDocumentType === type) {
      setSelectedDocumentType(null);
    } else {
      setSelectedDocumentType(type);
    }
  };

  // Month navigation handlers
  const handlePreviousMonth = () => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() - 1);
      return newMonth;
    });
  };

  const handleNextMonth = () => {
    const now = new Date();
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + 1);

      // Don't allow future months
      if (newMonth > currentMonthEnd) {
        return prev;
      }
      return newMonth;
    });
  };

  // Get month/year string for display
  const getMonthYearString = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  // Get start and end dates for current month
  const getMonthDateRange = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    return {
      startTime: start.toISOString(),
      endTime: end.toISOString(),
    };
  };

  // Helper function to find a document by matching file URL.
  // Searches the filtered list first, then falls back to the unfiltered list
  // so documents opened from message attachments can always be resolved.
  const findDocumentByUrl = (mediaUrl: string) => {
    if (!mediaUrl) return null;

    const getBaseUrl = (url: string) => {
      try {
        const urlObj = new URL(url);
        return urlObj.origin + urlObj.pathname;
      } catch {
        return url.split("?")[0];
      }
    };

    const mediaBaseUrl = getBaseUrl(mediaUrl);

    const matchInList = (docs: typeof patientDocuments) =>
      docs?.find((doc) => getBaseUrl(doc.file) === mediaBaseUrl) || null;

    return matchInList(patientDocuments) || matchInList(allPatientDocuments);
  };

  // Handle categorization type selection from dropdown - opens confirmation dialog
  const handleCategorizeTypeSelect = (type: DocumentType) => {
    setPendingCategorizeType(type);
    setIsCategorizeDialogOpen(true);
  };

  // Confirm and execute document categorization
  const handleConfirmCategorize = async () => {
    if (!pendingCategorizeType) return;

    if (!selectedDocument?.id) {
      toast({
        title: "Unable to categorize",
        description: "Document ID not found. Please try again.",
        variant: "destructive",
      });
      setIsCategorizeDialogOpen(false);
      setPendingCategorizeType(null);
      return;
    }

    setIsCategorizing(true);
    try {
      await apiService.categorizeDocument(
        selectedDocument.id,
        pendingCategorizeType
      );

      // Update the local state to reflect the new type
      setViewerDocumentType(pendingCategorizeType);
      setSelectedDocument((prev) =>
        prev ? { ...prev, documentType: pendingCategorizeType } : null
      );

      // Close the confirmation dialog
      setIsCategorizeDialogOpen(false);
      setPendingCategorizeType(null);

      // Refresh both document lists (stay on the preview)
      refetchDocuments();
      refetchAllDocuments();

      toast({
        title: "Document categorized",
        description: `Document categorized as ${getDocumentTypeName(pendingCategorizeType)}.`,
      });
    } catch (error) {
      console.error("Failed to categorize document:", error);
      toast({
        title: "Categorization failed",
        description: "Could not categorize the document. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCategorizing(false);
    }
  };

  // Cancel categorization
  const handleCancelCategorize = () => {
    setIsCategorizeDialogOpen(false);
    setPendingCategorizeType(null);
  };

  // Handle date selection - opens confirmation dialog
  const handleDateSelect = (date: Date) => {
    setPendingDate(date);
    setViewerCalendarOpen(false);
    setIsDateChangeDialogOpen(true);
  };

  // Confirm date change - calls the categorize API with the new document_date
  const handleConfirmDateChange = async () => {
    if (!pendingDate) return;

    if (!selectedDocument?.id) {
      toast({
        title: "Unable to update date",
        description: "Document ID not found. Please try again.",
        variant: "destructive",
      });
      setIsDateChangeDialogOpen(false);
      setPendingDate(null);
      return;
    }

    setIsDateChanging(true);
    try {
      const dateStr = format(pendingDate, "yyyy-MM-dd");
      await apiService.categorizeDocument(
        selectedDocument.id,
        selectedDocument.documentType || "UC",
        dateStr
      );

      // Update local state to reflect the new date
      setViewerDocumentDate(pendingDate);
      setSelectedDocument((prev) =>
        prev ? { ...prev, documentDate: pendingDate } : null
      );

      // Refresh both document lists
      refetchDocuments();
      refetchAllDocuments();

      toast({
        title: "Date updated",
        description: `Document date changed to ${format(pendingDate, "dd MMMM yyyy")}.`,
      });
    } catch (error) {
      console.error("Failed to update document date:", error);
      toast({
        title: "Date update failed",
        description: "Could not update the document date. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDateChanging(false);
      setIsDateChangeDialogOpen(false);
      setPendingDate(null);
    }
  };

  // Cancel date change
  const handleCancelDateChange = () => {
    setIsDateChangeDialogOpen(false);
    setPendingDate(null);
  };

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const markAsReadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollHeight = useRef<number>(0);
  const initialScrollDoneRef = useRef<boolean>(false);

  // Callback ref for messages container to handle immediate scroll
  const setMessagesContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      messagesContainerRef.current = node;
      // Immediately scroll to bottom when container mounts/updates
      if (!initialScrollDoneRef.current) {
        node.scrollTop = node.scrollHeight;
        initialScrollDoneRef.current = true;
      }
    }
  }, []);

  // Hooks
  const { toast } = useToast();
  const { user, hasAnyPermission, hasPermission, isStaffAdmin, isPatient } =
    useAuthStore();
  const canCreateNoteMessages = hasPermission(CONSOLE_PERMISSIONS.CREATE_NOTES);
  const canAssignSupportChats = hasPermission(CONSOLE_PERMISSIONS.ASSIGN_CHATS);
  const canTransferSupportChats = hasPermission(
    CONSOLE_PERMISSIONS.TRANSFER_CHATS
  );
  const canManageChats = hasAnyPermission([
    CONSOLE_PERMISSIONS.ASSIGN_CHATS,
    CONSOLE_PERMISSIONS.TRANSFER_CHATS,
  ]);
  const { data: reassignmentTargets, isLoading: isLoadingTargets } =
    useReassignmentTargets(conversationId, !isInternal && canManageChats);
  const reassignChat = useReassignChat();

  const chatId = conversationId;

  // Reset view state when chat changes
  useEffect(() => {
    setIsUserViewingHistory(false);
    setSearchQuery("");
    setShowSearch(false);
    setIsLoadingNewMessage(false);

    // Clear any pending mark as read timeouts
    if (markAsReadTimeoutRef.current) {
      clearTimeout(markAsReadTimeoutRef.current);
    }

    // Reset mark-as-read tracking
    isMarkingAsReadRef.current = false;
    lastMarkedMessageIdRef.current = null;

    // Reset scroll position tracking
    lastScrollHeight.current = 0;
    initialScrollDoneRef.current = false; // Reset for new chat

    // Immediately scroll to bottom when chat changes to prevent showing top first
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [chatId]);

  // Fetch chat detail - conditionally use support or internal chat hooks
  const {
    data: supportChatDetail,
    isLoading: supportChatLoading,
    error: supportChatError,
  } = usePingChatDetail(
    !isInternal && chatId ? chatId : "",
    !isInternal && !!chatId
  );
  const {
    data: internalChatDetail,
    isLoading: internalChatLoading,
    error: internalChatError,
  } = useInternalChatDetail(
    isInternal && chatId ? chatId : "",
    isInternal && !!chatId
  );

  // Use appropriate chat detail based on type
  const chatDetail = isInternal ? internalChatDetail : supportChatDetail;
  const chatLoading = isInternal ? internalChatLoading : supportChatLoading;
  const chatError = isInternal ? internalChatError : supportChatError;

  // Fetch messages with infinite scrolling - conditionally use support or internal hooks
  const supportMessagesQuery = usePingMessagesInfinite(
    !isInternal && chatId ? chatId : "",
    50
  );
  const internalMessagesQuery = useInternalMessagesInfinite(
    isInternal && chatId ? chatId : "",
    50
  );

  // Use appropriate messages query based on type
  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = isInternal ? internalMessagesQuery : supportMessagesQuery;

  // Send message mutations - conditionally use support or internal hooks
  const supportSendTextMessage = useSendTextMessage();
  const supportSendImageMessage = useSendImageMessage();
  const supportSendDocumentMessage = useSendDocumentMessage();

  const internalSendTextMessage = useSendInternalTextMessage();
  const internalSendImageMessage = useSendInternalImageMessage();
  const internalSendDocumentMessage = useSendInternalDocumentMessage();

  // Use appropriate mutations based on type
  const sendTextMessage = isInternal
    ? internalSendTextMessage
    : supportSendTextMessage;
  const sendImageMessage = isInternal
    ? internalSendImageMessage
    : supportSendImageMessage;
  const sendDocumentMessage = isInternal
    ? internalSendDocumentMessage
    : supportSendDocumentMessage;

  // Real-time updates with STABLE callback using useRef to prevent dependency loops
  // This callback should NEVER change, preventing infinite resubscriptions
  const handleNewMessageReceivedRef = useRef<(() => void) | undefined>(
    undefined
  );

  handleNewMessageReceivedRef.current = () => {
    // Don't set loading state if we're already viewing history
    // This prevents blocking mark-as-read when user is scrolled up
    if (isUserViewingHistory) {
      return;
    }

    setIsLoadingNewMessage(true);

    // Set a shorter timeout to clear loading state
    setTimeout(() => {
      setIsLoadingNewMessage(false);
    }, 500);
  };

  // Create a stable callback that won't change
  const stableHandleNewMessageReceived = useCallback(() => {
    handleNewMessageReceivedRef.current?.();
  }, []); // Empty deps - this function NEVER changes

  // Real-time updates for both support and internal chats
  const realtimeSupport = useRealtimeMessages(
    chatId || undefined,
    stableHandleNewMessageReceived,
    isInternal
  );
  const { sendTypingIndicator, markAsRead, isConnected, connectionState } =
    realtimeSupport;

  // Chat management mutations - conditionally use support or internal hooks
  const supportUpdateChatStatus = useUpdateChatStatus();
  const supportTransferChat = useTransferChat();
  const supportMarkMessagesAsRead = useMarkMessagesAsRead();
  const supportStarChat = useStarChat();
  const supportMarkChatAsUnread = useMarkChatAsUnread();

  const internalMarkMessagesAsRead = useMarkInternalMessagesAsRead();
  const internalStarChat = useStarInternalChat();
  const internalMarkChatAsUnread = useMarkInternalChatAsUnread();

  // Use appropriate mutations based on type (internal chats don't have status/transfer)
  const updateChatStatus = isInternal ? undefined : supportUpdateChatStatus;
  const transferChat = isInternal ? undefined : supportTransferChat;
  const markMessagesAsRead = isInternal
    ? internalMarkMessagesAsRead
    : supportMarkMessagesAsRead;
  const starChat = isInternal ? internalStarChat : supportStarChat;
  const markChatAsUnread = isInternal
    ? internalMarkChatAsUnread
    : supportMarkChatAsUnread;

  // Extract data with memoization to avoid dependency issues
  const messages = useMemo(
    () => messagesData?.messages || [],
    [messagesData?.messages]
  );
  const chat = chatDetail as SupportChatDetail | InternalChatDetail | undefined;
  const supportChat = !isInternal
    ? (chat as SupportChatDetail | undefined)
    : undefined;
  const selectableReassignmentTargets = useMemo<ReassignmentTarget[]>(() => {
    if (isInternal || !reassignmentTargets) {
      return [];
    }

    return reassignmentTargets.filter(
      (target) =>
        target.id !== supportChat?.care_manager && target.id !== user?.id
    );
  }, [isInternal, reassignmentTargets, supportChat?.care_manager, user?.id]);
  const selectedAssignmentLabel = useMemo(() => {
    if (!supportChat?.care_manager) {
      return "Unassigned";
    }

    if (supportChat.care_manager === user?.id) {
      return "You";
    }

    return supportChat.care_manager_name || "Assigned";
  }, [supportChat?.care_manager, supportChat?.care_manager_name, user?.id]);

  // Get participant data - for support chats it's patient, for internal it's other_participant
  const patient =
    !isInternal && chat ? (chat as SupportChatDetail)?.user : undefined;
  const otherParticipant =
    isInternal && chat
      ? (chat as InternalChatDetail)?.other_participant
      : undefined;

  // Helper function to determine if a message is from the current user
  // For support chats: uses is_from_user field
  // For internal chats: compares sender ID with current user ID, with fallback to sender_name
  const isMessageFromCurrentUser = useCallback(
    (message: Message | InternalMessage): boolean => {
      if (isInternal) {
        const internalMessage = message as InternalMessage;
        // Check sender_name first for optimistic messages (immediate detection)
        if (internalMessage.sender_name === "You") {
          return true;
        }
        // For internal messages, check if sender matches current user ID
        // Handle both string and number types (sender is number, user.id is string)
        if (user?.id && internalMessage.sender !== undefined) {
          const userIdNum =
            typeof user.id === "string" ? parseInt(user.id, 10) : user.id;
          const userIdStr = String(user.id);
          return (
            internalMessage.sender === userIdNum ||
            String(internalMessage.sender) === userIdStr
          );
        }
        return false;
      } else {
        const supportMessage = message as Message;
        // For support messages, is_from_user=false means it's from care manager (current user)
        return !supportMessage.is_from_user;
      }
    },
    [isInternal, user?.id]
  );

  // Fetch patient documents - only for support chats
  const {
    data: patientDocuments,
    isLoading: documentsLoading,
    refetch: refetchDocuments,
  } = usePatientDocuments(
    !isInternal && patient?.id ? patient.id : undefined,
    !isInternal
      ? {
          // When "All" is selected, use consolidated=false and include_uncategorized=true
          // When a specific type is selected, filter by that type
          types: selectedDocumentType ? [selectedDocumentType] : undefined,
          consolidated: false,
          includeUncategorized:
            selectedDocumentType === null ? true : undefined,
          ...getMonthDateRange(currentMonth),
        }
      : undefined
  );

  // Unfiltered document list for URL-to-ID resolution when opening docs from message attachments.
  // The filtered query above may not contain the document if it's outside the selected month/type.
  const { data: allPatientDocuments, refetch: refetchAllDocuments } =
    usePatientDocuments(
      !isInternal && patient?.id ? patient.id : undefined,
      !isInternal
        ? { consolidated: false, includeUncategorized: true }
        : undefined
    );

  // Debug logging for care manager assignment - removed for production

  // Sort messages in chronological order (oldest first) and apply search filter
  const filteredMessages = useMemo(() => {
    // First, sort messages by created_at in ascending order (oldest first)
    const sortedMessages = [...messages] as (Message | InternalMessage)[];
    sortedMessages.sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    // Then apply search filter if query exists
    if (!searchQuery.trim()) return sortedMessages;

    return sortedMessages.filter(
      (message) =>
        message.text_content
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        message.sender_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [messages, searchQuery]);

  // Unified scroll management - handles initial load, chat changes, and new messages
  useEffect(() => {
    if (!messagesContainerRef.current || filteredMessages.length === 0) return;

    // If we just loaded more messages (infinite scroll), preserve scroll position
    if (
      lastScrollHeight.current > 0 &&
      messagesContainerRef.current.scrollHeight > lastScrollHeight.current
    ) {
      const newScrollTop =
        messagesContainerRef.current.scrollHeight - lastScrollHeight.current;
      messagesContainerRef.current.scrollTop = newScrollTop;
      lastScrollHeight.current = 0;
      return;
    }

    // Only auto-scroll to bottom if user is not viewing history and messages have loaded
    if (!isUserViewingHistory && !messagesLoading) {
      const container = messagesContainerRef.current;

      // Check if content is scrollable
      const isScrollable = container.scrollHeight > container.clientHeight + 10;

      if (isScrollable) {
        const isAtBottom =
          container.scrollHeight - container.scrollTop <=
          container.clientHeight + 50;

        // Only scroll if not already at bottom to prevent infinite scrolling
        if (!isAtBottom) {
          // Use requestAnimationFrame to ensure DOM is fully rendered
          requestAnimationFrame(() => {
            if (!messagesContainerRef.current) return;

            // Check current scroll position to determine behavior
            const currentScrollTop = messagesContainerRef.current.scrollTop;
            const maxScroll =
              messagesContainerRef.current.scrollHeight -
              messagesContainerRef.current.clientHeight;

            // If we're near the top (first load scenario), jump instantly to bottom
            // Otherwise use smooth scroll for new messages
            if (currentScrollTop < 100) {
              // Instant scroll for initial load
              messagesContainerRef.current.scrollTop =
                messagesContainerRef.current.scrollHeight;
            } else if (Math.abs(currentScrollTop - maxScroll) > 50) {
              // Smooth scroll only if we're not already near bottom
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "end",
                });
              }
            }
          });
        }
      } else {
        // For non-scrollable content (few messages), ensure we're at natural bottom position
        // This handles the case where messages fit in one screen
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [chatId, filteredMessages.length, messagesLoading, isUserViewingHistory]);

  // Additional effect to ensure scroll to bottom after complete render (fallback for edge cases)
  useEffect(() => {
    if (
      !messagesContainerRef.current ||
      filteredMessages.length === 0 ||
      messagesLoading
    )
      return;

    // Use a microtask to run after React finishes painting
    const timeoutId = setTimeout(() => {
      if (messagesContainerRef.current && !isUserViewingHistory) {
        const container = messagesContainerRef.current;
        // Aggressively scroll to bottom on initial loads
        if (
          container.scrollTop <
          container.scrollHeight - container.clientHeight - 10
        ) {
          container.scrollTop = container.scrollHeight;
          initialScrollDoneRef.current = true;
        }
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [filteredMessages.length, messagesLoading, isUserViewingHistory, chatId]);

  // Handle scroll detection for infinite loading and history viewing
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 50; // 50px threshold
      const isAtTop = scrollTop <= 100; // 100px threshold from top

      // Check if content is actually scrollable (has overflow)
      const isScrollable = scrollHeight > clientHeight + 10; // 10px buffer to account for minor variations

      // Only set viewing history flag if content is scrollable
      // This prevents shake effect when all messages fit on one screen
      if (isScrollable) {
        // If user scrolls up significantly, they're viewing history
        setIsUserViewingHistory(!isAtBottom);
      } else {
        // If not scrollable, always consider user at bottom (not viewing history)
        setIsUserViewingHistory(false);
      }

      // Load more messages when scrolling near the top (only if scrollable and has more pages)
      if (
        isScrollable &&
        isAtTop &&
        hasNextPage &&
        !isFetchingNextPage &&
        !messagesLoading
      ) {
        // Store current scroll position before loading new content
        lastScrollHeight.current = scrollHeight;

        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, messagesLoading, fetchNextPage]
  );

  // Reset history viewing when chat changes
  useEffect(() => {
    setIsUserViewingHistory(false);
  }, [chatId]);

  // Clear loading state when messages actually update
  useEffect(() => {
    // Only clear if we were loading and messages have actually updated
    if (isLoadingNewMessage) {
      // Clear the loading state since messages have updated
      const timer = setTimeout(() => {
        setIsLoadingNewMessage(false);
      }, 100); // Small delay to ensure DOM update

      return () => clearTimeout(timer);
    }
  }, [messages, isLoadingNewMessage]);

  // Typing indicator effect
  useEffect(() => {
    // This would typically come from real-time updates
    // For now, we'll simulate typing indicators
    const interval = setInterval(() => {
      setIsTypingIndicatorVisible(Math.random() > 0.8);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleAddNote = async () => {
    if (!note.trim() || !chatId) return;
    if (!canCreateNoteMessages) {
      toast({
        title: "Access Denied",
        description: "You do not have permission to create internal notes.",
        variant: "destructive",
      });
      return;
    }

    // Store current note before clearing
    const currentNote = note;

    // Clear form immediately for better UX
    setNote("");
    setShowNoteInput(false);

    try {
      await sendTextMessage.mutateAsync({
        chatId,
        text: currentNote,
        isNote: true,
      });
    } catch (error) {
      const apiError = error as { status?: number; message?: string };
      logger.error("Failed to add note:", error);
      // Restore form state on error
      setNote(currentNote);
      setShowNoteInput(true);
      toast({
        title: apiError.status === 403 ? "Access Denied" : "Error",
        description:
          apiError.status === 403
            ? "Your permissions no longer allow internal note creation."
            : apiError.message || "Failed to add internal note.",
        variant: "destructive",
      });
    }
  };

  const handleSendReply = async () => {
    if (!chatId) return;

    // Store current message and files before clearing
    const currentMessage = message;
    const currentFiles = [...selectedFiles];
    const currentPreviews = [...filePreviews];

    // Clear form immediately for better UX
    setMessage("");
    setSelectedFiles([]);
    setFilePreviews([]);

    try {
      if (currentFiles.length > 0) {
        // Send files sequentially with text attached to first file only
        for (let i = 0; i < currentFiles.length; i++) {
          const file = currentFiles[i];
          const isFirstFile = i === 0;

          // Send file message with text content attached to first file only
          if (isImageFile(file.name)) {
            await sendImageMessage.mutateAsync({
              chatId,
              imageFile: file,
              textContent: isFirstFile
                ? currentMessage.trim() || undefined
                : undefined,
              isNote: false,
            });
          } else {
            await sendDocumentMessage.mutateAsync({
              chatId,
              documentFile: file,
              textContent: isFirstFile
                ? currentMessage.trim() || undefined
                : undefined,
              isNote: false,
            });
          }
        }
      } else if (currentMessage.trim()) {
        // Send text-only message
        await sendTextMessage.mutateAsync({
          chatId,
          text: currentMessage,
          isNote: false,
        });
      }
    } catch (error) {
      logger.error("Failed to send message:", error);
      // Restore form state on error
      setMessage(currentMessage);
      setSelectedFiles(currentFiles);
      setFilePreviews(currentPreviews);
    }
  };

  // File handling functions
  const handleFileSelect = (newFiles: File[]) => {
    // Limit to MAX_FILES and combine with existing files
    const availableSlots = MAX_FILES - selectedFiles.length;
    const filesToAdd = newFiles.slice(0, availableSlots);

    if (filesToAdd.length === 0) {
      // Show error message if no slots available
      logger.error(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const updatedFiles = [...selectedFiles, ...filesToAdd];
    setSelectedFiles(updatedFiles);

    // Create previews for images
    const newPreviews = [...filePreviews];
    filesToAdd.forEach((file, index) => {
      if (isImageFile(file.name)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const previewIndex = selectedFiles.length + index;
          newPreviews[previewIndex] = e.target?.result as string;
          setFilePreviews([...newPreviews]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files);
    }
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const removeSelectedFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    const updatedPreviews = filePreviews.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    setFilePreviews(updatedPreviews);
  };

  const removeAllSelectedFiles = () => {
    setSelectedFiles([]);
    setFilePreviews([]);
  };

  // Typing indicator handling
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTyping = () => {
    sendTypingIndicator(true);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false);
    }, 1000);
  };

  // Helper function to check if current user can mark messages as read
  // For support chats: user must be the assigned care_manager
  // For internal chats: user must be one of the participants
  const canMarkAsRead = useCallback((): boolean => {
    if (!chat || !user?.id) {
      return false;
    }

    if (isInternal) {
      const internalChat = chat as InternalChatDetail;
      // Try both string and number comparison since user.id might be string or number
      const userIdNum =
        typeof user.id === "string" ? parseInt(user.id, 10) : user.id;
      const userIdStr = String(user.id);
      // Check if current user is participant_1 or participant_2 (comparing as both number and string)
      const isParticipant =
        internalChat.participant_1 === userIdNum ||
        internalChat.participant_2 === userIdNum ||
        String(internalChat.participant_1) === userIdStr ||
        String(internalChat.participant_2) === userIdStr;
      return isParticipant;
    } else {
      const supportChat = chat as SupportChatDetail;
      // Staff admins can manage any support chat. Care managers must be assigned.
      return isStaffAdmin() || supportChat.care_manager === user.id;
    }
  }, [chat, user?.id, isInternal, isStaffAdmin]);

  // Mark messages as read function with re-entrance prevention
  const markMessagesAsReadHandler = useCallback(() => {
    // Guard against re-entrant calls
    if (isMarkingAsReadRef.current) {
      return;
    }

    // Only mark as read if we're viewing the current chat and not looking at history
    if (
      !chatId ||
      !chat ||
      !isViewActive ||
      isUserViewingHistory ||
      isLoadingNewMessage
    ) {
      return;
    }

    // IMPORTANT: Only call read API when user is authorized
    // For support chats: user must be assigned care_manager
    // For internal chats: user must be one of the participants
    if (!canMarkAsRead()) {
      return;
    }

    // Only mark as read if there are unread messages (same for both support and internal chats)
    // This ensures we only mark when opening/viewing the chat, not on every message arrival
    const shouldMarkAsRead = chat.unread_count > 0 && messages.length > 0;

    if (shouldMarkAsRead) {
      // Filter out optimistic messages with temp IDs (they start with "temp-")
      // We should only mark real messages from the server as read
      const realMessages = messages.filter(
        (msg) => !msg.id.startsWith("temp-")
      );

      if (realMessages.length === 0) {
        // No real messages yet, skip marking as read
        return;
      }

      // Sort messages by creation time to get the actual latest real message
      const sortedMessages = [...realMessages].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      const latestMessage = sortedMessages[0];

      // Don't mark as read if it's the same message we just marked
      if (
        latestMessage &&
        latestMessage.id !== lastMarkedMessageIdRef.current
      ) {
        isMarkingAsReadRef.current = true;
        lastMarkedMessageIdRef.current = latestMessage.id;

        markMessagesAsRead.mutate(
          { messageId: latestMessage.id, chatId },
          {
            onSettled: () => {
              // Allow next mark as read after a delay
              setTimeout(() => {
                isMarkingAsReadRef.current = false;
              }, 1000); // 1 second cooldown
            },
          }
        );
      }
    }
  }, [
    chatId,
    chat,
    isViewActive,
    isUserViewingHistory,
    isLoadingNewMessage,
    messages,
    markMessagesAsRead,
    canMarkAsRead,
    isInternal,
  ]);

  // Handle toggling chat read/unread status - only available for authorized users
  const handleToggleUnread = useCallback(async () => {
    if (!chatId) return;

    // Only allow if user is authorized (care_manager for support chats, participant for internal chats)
    if (!canMarkAsRead()) {
      return;
    }

    try {
      await markChatAsUnread.mutateAsync({ chatId });
    } catch (error) {
      logger.error("Failed to toggle chat read/unread status:", error);
    }
  }, [chatId, canMarkAsRead, markChatAsUnread]);

  // Mark messages as read when viewing conversation - matches Flutter timing
  // Only when logged-in user is authorized (care_manager for support chats, participant for internal chats)
  // Only triggers when opening/viewing the chat or when unread_count changes, not on every message arrival
  useEffect(() => {
    // Only proceed if user is authorized
    if (!canMarkAsRead()) {
      return;
    }

    // Only mark as read when there are unread messages and the view is active
    if (
      chatId &&
      chat &&
      chat.unread_count > 0 &&
      messages.length > 0 &&
      isViewActive &&
      !isUserViewingHistory &&
      !isLoadingNewMessage
    ) {
      // Clear any existing timeout
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
      }

      // Set timeout to mark as read after messages are loaded
      // Reduced timeout for better responsiveness
      markAsReadTimeoutRef.current = setTimeout(() => {
        markMessagesAsReadHandler();
      }, 800);

      return () => {
        if (markAsReadTimeoutRef.current) {
          clearTimeout(markAsReadTimeoutRef.current);
        }
      };
    }
  }, [
    chatId,
    chat?.unread_count,
    isViewActive,
    isUserViewingHistory,
    isLoadingNewMessage,
    markMessagesAsReadHandler,
    canMarkAsRead,
    messages.length,
  ]);

  // Handle view lifecycle changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsViewActive(!document.hidden);
    };

    const handleFocus = () => setIsViewActive(true);
    const handleBlur = () => setIsViewActive(false);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Chat management functions
  const handleReassignChat = async (careManagerId: string) => {
    if (!chatId) return;

    const requiredPermission = supportChat?.care_manager
      ? canTransferSupportChats
      : canAssignSupportChats;
    if (!requiredPermission) {
      toast({
        title: "Access Denied",
        description: supportChat?.care_manager
          ? "You do not have permission to transfer chats."
          : "You do not have permission to assign chats.",
        variant: "destructive",
      });
      return;
    }

    try {
      await reassignChat.mutateAsync({
        chatId,
        careManagerId,
      });
    } catch (error) {
      logger.error("Failed to reassign chat:", error);
    }
  };

  const handleStatusChange = async (
    newStatus: "open" | "closed" | "resolved" | "assigned"
  ) => {
    if (!chatId) return;

    try {
      await updateChatStatus.mutateAsync({
        chatId,
        status: newStatus,
      });
    } catch (error) {
      logger.error("Failed to update chat status:", error);
    }
  };

  const handleTransferChat = async (toCareManagerId: string) => {
    if (!chatId) return;

    try {
      await transferChat.mutateAsync({
        fromChatId: chatId,
        toCareManagerId,
      });
    } catch (error) {
      logger.error("Failed to transfer chat:", error);
    }
  };

  const hasConversationSnapshot = Boolean(chat);
  const hasMessagesSnapshot = typeof messagesData !== "undefined";

  // Loading state - keep the current conversation mounted during background refetches
  if (
    (chatLoading && !hasConversationSnapshot) ||
    (messagesLoading && !hasMessagesSnapshot)
  ) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="z-body text-muted-foreground">
            Loading conversation...
          </span>
        </div>
      </div>
    );
  }

  // Error state
  if (chatError || messagesError) {
    return (
      <Card className="flex min-h-0 flex-1 items-center justify-center">
        <div className="flex flex-col items-center space-y-2">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <span className="z-body text-destructive">
            Failed to load conversation
          </span>
          <Button
            variant="outline"
            size="sm"
            className="z-button"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  // No chat selected state
  if (!chatId || !chat) {
    return (
      <Card className="flex min-h-0 flex-1 items-center justify-center">
        <div className="text-center">
          <h3 className="z-h2 mb-2 text-foreground">Select a conversation</h3>
          <p className="z-body text-muted-foreground">
            Choose a chat from the sidebar to start messaging
          </p>
        </div>
      </Card>
    );
  }

  // Get the participant (patient for support chats, otherParticipant for internal chats)
  const participant = isInternal ? otherParticipant : patient;

  // Safety check: For support chats, we need patient data. For internal chats, we can proceed even without otherParticipant
  // Only block if it's a support chat without patient (support chats require patient data)
  if (!isInternal && !patient && chat) {
    // Only show loading if we have a support chat but no patient yet (still loading)
    return (
      <Card className="flex min-h-0 flex-1 items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="z-body text-muted-foreground">Updating chat...</span>
        </div>
      </Card>
    );
  }

  // Get display name and initials from appropriate participant
  const participantName = isInternal
    ? `${otherParticipant?.first_name || ""} ${otherParticipant?.last_name || ""}`.trim() ||
      "Unknown User"
    : `${patient?.first_name || ""} ${patient?.last_name || ""}`.trim() ||
      "Unknown Patient";
  const participantInitials = isInternal
    ? getUserInitials(otherParticipant?.first_name, otherParticipant?.last_name)
    : getUserInitials(patient?.first_name, patient?.last_name);

  // Keep backward compatibility for variables used elsewhere
  const patientName = participantName;
  const patientInitials = participantInitials;
  const patientDemographics = [
    (supportChat as any)?.age ? `${(supportChat as any).age}y` : null,
    (supportChat as any)?.gender === "M"
      ? "Male"
      : (supportChat as any)?.gender === "F"
        ? "Female"
        : (supportChat as any)?.gender || null,
  ].filter(Boolean);
  const patientLocation = (supportChat as any)?.city || null;
  const patientQuickActions = [
    { id: "emr", label: "EMR" },
    { id: "bookings", label: "Bookings" },
    { id: "transactions", label: "Transactions" },
    { id: "more", label: "More" },
  ] as const;
  const isComposerBusy =
    sendTextMessage.isPending ||
    sendImageMessage.isPending ||
    sendDocumentMessage.isPending;
  const activeComposerText = showNoteInput ? note : message;
  const canAttachMoreFiles = selectedFiles.length < MAX_FILES;
  const canUseComposerNotes = canCreateNoteMessages;
  const canSendComposer =
    !isComposerBusy &&
    (showNoteInput
      ? activeComposerText.trim().length > 0
      : selectedFiles.length > 0 || activeComposerText.trim().length > 0);

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 gap-2 xl:gap-3">
      {/* Main Conversation */}
      <Card className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-3xl border-border/60 gap-0 py-0">
        {/* Header */}
        <div className="border-b border-border px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex items-center justify-between gap-1.5 sm:gap-2">
            <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-1">
              <div className="z-secondary flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground sm:h-9 sm:w-9 lg:h-10 lg:w-10">
                {patientInitials}
              </div>
              <h2 className="z-body-md truncate text-foreground">
                {patientName}
              </h2>
            </div>
            <div className="flex items-center gap-0.5 sm:gap-1 md:gap-1.5 shrink-0">
              {/* Care Manager Assignment Dropdown - Only for console users and only for support chats */}
              {!isInternal &&
                !isPatient() &&
                (supportChat?.care_manager
                  ? canTransferSupportChats
                  : canAssignSupportChats) && (
                  <div className="hidden lg:flex items-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          disabled={reassignChat.isPending || isLoadingTargets}
                          className="z-caption h-8 min-w-[10rem] max-w-[12rem] px-2.5 flex items-center justify-between gap-2 rounded-lg border-border bg-background shadow-none focus-visible:ring-2 focus-visible:ring-primary/15"
                        >
                          <div
                            className={cn(
                              "flex min-w-0 flex-1 items-center gap-2",
                              supportChat?.care_manager
                                ? "text-foreground"
                                : "text-muted-foreground"
                            )}
                          >
                            {supportChat?.care_manager ? (
                              <UserCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
                            ) : (
                              <UserX className="h-3.5 w-3.5 shrink-0" />
                            )}
                            <span className="truncate">
                              {selectedAssignmentLabel}
                            </span>
                          </div>
                          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-72" align="end">
                        <DropdownMenuItem
                          disabled
                          className="text-muted-foreground focus:bg-transparent"
                        >
                          <div className="flex w-full items-center justify-between gap-2 py-0.5">
                            <span className="flex min-w-0 items-center gap-2">
                              {supportChat?.care_manager ? (
                                <UserCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
                              ) : (
                                <UserX className="h-3.5 w-3.5 shrink-0" />
                              )}
                              <span className="truncate">
                                {selectedAssignmentLabel}
                              </span>
                            </span>
                            <Badge
                              variant="outline"
                              className="z-caption h-4 shrink-0 border-border bg-muted px-1 text-muted-foreground"
                            >
                              {supportChat?.care_manager
                                ? "Current"
                                : "Unassigned"}
                            </Badge>
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {isLoadingTargets ? (
                          <div className="flex items-center justify-center p-4">
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          </div>
                        ) : selectableReassignmentTargets.length === 0 ? (
                          <div className="z-caption p-2 text-center text-muted-foreground">
                            No available targets
                          </div>
                        ) : (
                          selectableReassignmentTargets.map((target) => (
                            <DropdownMenuItem
                              key={target.id}
                              onClick={() => handleReassignChat(target.id)}
                              className="cursor-pointer"
                            >
                              <div className="flex w-full items-center justify-between gap-2 py-0.5">
                                <span className="flex min-w-0 items-center gap-2">
                                  <UserCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
                                  <span className="truncate">
                                    {target.id === user?.id
                                      ? "You"
                                      : target.name}
                                  </span>
                                </span>
                                <Badge
                                  variant="outline"
                                  className="z-caption h-4 shrink-0 border-border bg-muted px-1 text-muted-foreground"
                                >
                                  {getReassignmentRoleLabel(target.role)}
                                </Badge>
                              </div>
                            </DropdownMenuItem>
                          ))
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}

              {/* Search Toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 border border-border rounded-md hover:bg-muted shrink-0"
                onClick={() => setShowSearch(!showSearch)}
                title="Search messages"
              >
                <Search className="h-3.5 w-3.5" />
              </Button>

              {/* Patient Info Toggle - Only for support chats */}
              {!isInternal && (
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-8 w-8 shrink-0 rounded-md border border-border hover:bg-muted",
                    showPatientInfo &&
                      "bg-primary/10 border-primary text-primary"
                  )}
                  onClick={() => setShowPatientInfo(!showPatientInfo)}
                  title={
                    showPatientInfo ? "Hide patient info" : "Show patient info"
                  }
                >
                  <Info className="h-3.5 w-3.5" />
                </Button>
              )}

              {/* Star Button - Only visible when user is authorized (care_manager for support chats, participant for internal chats) */}
              {canMarkAsRead() && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden sm:flex h-8 w-8 border border-border rounded-md hover:bg-muted shrink-0"
                  onClick={() => chatId && starChat.mutate({ chatId })}
                  disabled={starChat.isPending}
                  title={
                    (chat as any)?.is_starred ? "Starred" : "Mark as starred"
                  }
                >
                  <Star
                    className={cn(
                      "h-3.5 w-3.5",
                      (chat as any)?.is_starred
                        ? "fill-primary text-primary"
                        : ""
                    )}
                  />
                </Button>
              )}
              {/* Chat Management Dropdown */}
              {canManageChats && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 border border-border rounded-md hover:bg-muted shrink-0"
                      title="More options"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem disabled className="z-body">
                      <Phone className="mr-2 h-4 w-4" />
                      Voice call
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled className="z-body">
                      <Video className="mr-2 h-4 w-4" />
                      Video call
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {/* Toggle Read/Unread - Only for authorized users */}
                    {canMarkAsRead() && (
                      <>
                        <DropdownMenuItem
                          onClick={handleToggleUnread}
                          disabled={markChatAsUnread.isPending}
                          className="z-body"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          {chat.is_unread ? "Mark as read" : "Mark as unread"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}

                    {/* Status Actions - Only for support chats */}
                    {!isInternal && (
                      <>
                        {(chat as SupportChatDetail).status !== "resolved" && (
                          <DropdownMenuItem
                            onClick={() => handleStatusChange("resolved")}
                            className="z-body"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark as resolved
                          </DropdownMenuItem>
                        )}

                        {(chat as SupportChatDetail).status !== "closed" && (
                          <DropdownMenuItem
                            onClick={() => handleStatusChange("closed")}
                            className="z-body"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Close chat
                          </DropdownMenuItem>
                        )}

                        {(chat as SupportChatDetail).status === "resolved" && (
                          <DropdownMenuItem
                            onClick={() => handleStatusChange("open")}
                            className="z-body"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Reopen chat
                          </DropdownMenuItem>
                        )}

                        {(chat as SupportChatDetail).status === "closed" && (
                          <DropdownMenuItem
                            onClick={() => handleStatusChange("open")}
                            className="z-body"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Reopen chat
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Search Bar */}
          {showSearch && (
            <div className="mt-3">
              <Input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
          )}
        </div>

        {/* Messages */}
        <div
          ref={setMessagesContainerRef}
          className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6"
          onScroll={handleScroll}
        >
          {/* Loading indicator for older messages */}
          {isFetchingNextPage && (
            <div className="flex justify-center py-4">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="z-body">Loading older messages...</span>
              </div>
            </div>
          )}

          {/* Search Results Info */}
          {searchQuery && (
            <div className="z-secondary mb-4 text-center text-muted-foreground">
              {filteredMessages.length} message
              {filteredMessages.length !== 1 ? "s" : ""} found
            </div>
          )}

          {/* Message Count Info - Only show if there are messages */}
          {!searchQuery &&
            messagesData?.pagination?.count !== undefined &&
            messagesData.pagination.count > 0 &&
            filteredMessages.length > 0 && (
              <div className="z-secondary mb-4 text-center text-muted-foreground">
                Showing {filteredMessages.length} of{" "}
                {messagesData.pagination.count} messages
                {hasNextPage && (
                  <span className="z-caption mt-1 block">
                    Scroll up to load older messages
                  </span>
                )}
              </div>
            )}

          {filteredMessages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="z-body mb-2 text-muted-foreground">
                  {searchQuery ? "No messages found" : "No messages yet"}
                </p>
                <p className="z-secondary text-muted-foreground/80">
                  {searchQuery
                    ? "Try a different search term"
                    : "Start the conversation by sending a message"}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Group messages by date */}
              {(() => {
                const groupedMessages: {
                  [key: string]: (Message | InternalMessage)[];
                } = {};
                filteredMessages.forEach((message) => {
                  const date = formatMessageDate(message.created_at);
                  if (!groupedMessages[date]) {
                    groupedMessages[date] = [];
                  }
                  groupedMessages[date].push(message);
                });

                return Object.entries(groupedMessages).map(
                  ([date, dateMessages]) => (
                    <div key={date}>
                      {/* Centering the Badge */}
                      <div className="flex justify-center mb-4">
                        <Badge className="z-caption tabular-nums border-border bg-muted text-muted-foreground hover:bg-muted">
                          {date}
                        </Badge>
                      </div>

                      {/* Messages for this date */}
                      <div className="space-y-4">
                        {dateMessages.map((message) => (
                          <div key={message.id}>
                            <MessageItem
                              message={message}
                              patientInitials={patientInitials}
                              isCurrentUser={isMessageFromCurrentUser(message)}
                              onMediaClick={(url, type, filename) => {
                                const isOwnMessage =
                                  isMessageFromCurrentUser(message);
                                const matchedDocument = findDocumentByUrl(url);
                                handleDocumentViewerOpen(
                                  matchedDocument?.id || "",
                                  url,
                                  filename,
                                  matchedDocument?.document_type || null,
                                  matchedDocument?.document_date || null,
                                  matchedDocument?.created_at ||
                                    message.created_at,
                                  isOwnMessage
                                );
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                );
              })()}

              {/* Scroll anchor for auto-scroll to bottom */}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Message Input Bar */}
        <div
          className={cn(
            "relative shrink-0 border-t border-border bg-card p-6",
            isDragOver && "bg-primary/10 border-primary/30"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDragOver && (
            <div className="absolute inset-0 bg-primary/10 bg-opacity-90 flex items-center justify-center z-10">
              <div className="text-center">
                <Paperclip className="h-8 w-8 text-primary mx-auto mb-2" />
                <p className="z-body-md text-primary">
                  Drop files here to upload
                </p>
                <p className="z-secondary text-primary/80">
                  {selectedFiles.length > 0
                    ? `${MAX_FILES - selectedFiles.length} more files allowed`
                    : `Up to ${MAX_FILES} files allowed`}
                </p>
              </div>
            </div>
          )}

          {/* File Preview Section */}
          {selectedFiles.length > 0 && (
            <div className="mb-3 rounded-lg border border-border bg-muted/40 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="z-body-md text-foreground">
                  Attached files ({selectedFiles.length}/{MAX_FILES}):
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeAllSelectedFiles}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                >
                  ×
                </Button>
              </div>

              <div className="space-y-2">
                {selectedFiles.map((file, index) => {
                  const preview = filePreviews[index];
                  return (
                    <div
                      key={index}
                      className="flex items-center space-x-2 rounded border border-border bg-background p-2"
                    >
                      {preview ? (
                        /* Image preview */
                        <img
                          src={preview}
                          alt={file.name}
                          className="w-12 h-12 object-cover rounded border"
                        />
                      ) : (
                        /* File icon */
                        getFileIcon(file.name)
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="z-body-md truncate text-foreground">
                          {file.name}
                        </p>
                        <p className="z-caption z-numeric text-muted-foreground">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSelectedFile(index)}
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                      >
                        ×
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*,application/pdf,.doc,.docx,.txt"
            multiple
            onChange={handleFileInputChange}
          />

          {/* Input Container */}
          <div
            className={cn(
              "rounded-lg border p-3 shadow-sm transition-colors",
              showNoteInput
                ? "border-warning/25 bg-warning/10"
                : "border-border/70 bg-card"
            )}
          >
            <div className="mb-2 flex flex-wrap items-start gap-2">
              {canUseComposerNotes ? (
                <div className="inline-flex h-9 items-center rounded-md border border-border bg-muted/50 p-0.5 gap-0.5">
                  <button
                    type="button"
                    disabled={isComposerBusy}
                    onClick={() => {
                      setShowNoteInput(false);
                      setNote("");
                    }}
                    className={cn(
                      "z-label inline-flex h-full items-center rounded px-3 transition-colors disabled:pointer-events-none disabled:opacity-50",
                      !showNoteInput
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    )}
                  >
                    Reply
                  </button>
                  <button
                    type="button"
                    disabled={isComposerBusy}
                    onClick={() => {
                      setShowNoteInput(true);
                      setMessage("");
                      removeAllSelectedFiles();
                    }}
                    className={cn(
                      "z-label inline-flex h-full items-center rounded px-3 transition-colors disabled:pointer-events-none disabled:opacity-50",
                      showNoteInput
                        ? "bg-warning/20 text-warning-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    )}
                  >
                    Note
                  </button>
                </div>
              ) : (
                <div className="z-label inline-flex h-9 items-center rounded-md border border-border bg-card px-3.5 text-foreground shadow-sm">
                  Reply
                </div>
              )}
            </div>

            <Textarea
              placeholder={
                showNoteInput ? "Add an internal note..." : "Write a reply..."
              }
              value={activeComposerText}
              onChange={(e) => {
                if (showNoteInput) {
                  setNote(e.target.value);
                } else {
                  setMessage(e.target.value);
                  if (e.target.value.trim()) {
                    handleTyping();
                  }
                }
              }}
              className="z-body min-h-14 max-h-32 resize-none border-none bg-transparent px-0 py-2 text-foreground shadow-none break-words overflow-wrap-anywhere placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
              rows={1}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (showNoteInput) {
                    handleAddNote();
                  } else {
                    handleSendReply();
                  }
                }
              }}
              style={{
                height: "auto",
                minHeight: "56px",
              }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = "auto";
                target.style.height = `${Math.min(
                  Math.max(target.scrollHeight, 56),
                  128
                )}px`;
              }}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <div className="flex flex-wrap items-center gap-2">
                {!showNoteInput && (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={isComposerBusy || !canAttachMoreFiles}
                          className="h-9 w-9 rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground active:bg-muted/80"
                          title={
                            canAttachMoreFiles
                              ? "Add"
                              : `Maximum ${MAX_FILES} files allowed`
                          }
                          aria-label="Add options"
                        >
                          <SiteIcon
                            name="plus"
                            className={iconography.size.inline}
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-44">
                        <DropdownMenuItem onClick={handleOpenFilePicker}>
                          <Paperclip className="h-4 w-4 text-muted-foreground" />
                          <span>Attach file</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {!isInternal && canUseComposerNotes && (
                      <TrackerSelectionDropdown
                        onTrackerSelect={handleTrackerSelect}
                        disabled={isComposerBusy}
                        triggerLabel="Tracker"
                        triggerClassName="rounded-md"
                      />
                    )}
                  </>
                )}
              </div>

              <Button
                size="icon"
                onClick={showNoteInput ? handleAddNote : handleSendReply}
                disabled={!canSendComposer}
                className={cn(
                  "h-9 w-9 rounded-full shadow-sm transition-colors",
                  canSendComposer
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "bg-muted text-muted-foreground hover:bg-muted"
                )}
                aria-label={showNoteInput ? "Add note" : "Send message"}
              >
                {isComposerBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowUp className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Right Sidebar - Only for support chats */}
      {!isInternal &&
        showPatientInfo &&
        (() => {
          const supportChat = chat as SupportChatDetail | undefined;
          return (
            <>
              {/* Overlay for small screens */}
              <div
                className="xl:hidden fixed inset-0 bg-black/50 z-40"
                onClick={() => setShowPatientInfo(false)}
              />
              {/* Patient Info Panel */}
              <aside className="fixed bottom-0 right-0 top-0 z-50 w-80 shrink-0 overflow-y-auto rounded-l-3xl border border-border/60 bg-card shadow-sm sm:w-96 xl:relative xl:top-auto xl:z-auto xl:w-80 xl:rounded-3xl 2xl:w-96">
                <div className="sticky top-0 z-10 bg-card">
                  <div className="space-y-5 border-b border-border px-6 py-6">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-14 w-14 border border-border/60">
                        <AvatarFallback className="z-h3 bg-muted text-foreground">
                          {patientInitials}
                        </AvatarFallback>
                      </Avatar>

                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="z-body-lg truncate font-medium text-foreground">
                              {patientName}
                            </h3>
                            <div className="mt-1 space-y-0.5">
                              <p className="z-secondary text-muted-foreground">
                                {patientDemographics.length > 0
                                  ? patientDemographics.join(" • ")
                                  : "Patient profile"}
                              </p>
                              {patientLocation ? (
                                <p className="z-secondary break-words text-muted-foreground">
                                  {patientLocation}
                                </p>
                              ) : null}
                            </div>
                          </div>

                          <div className="flex shrink-0 items-center gap-2">
                            {(supportChat as any)?.zarity_user_id ? (
                              <Badge
                                variant="secondary"
                                className="z-caption z-numeric rounded-full border-0 bg-muted px-3 py-1 text-foreground"
                              >
                                {(supportChat as any).zarity_user_id}
                              </Badge>
                            ) : null}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 rounded-full xl:hidden"
                              onClick={() => setShowPatientInfo(false)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <TooltipProvider delayDuration={150}>
                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            {patientQuickActions.map(({ id, label }) => (
                              <Tooltip key={id}>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label={label}
                                    aria-disabled="true"
                                    onClick={(event) => event.preventDefault()}
                                    className="h-8 w-8 shrink-0 rounded-md border border-border bg-background text-muted-foreground shadow-none hover:bg-muted hover:text-foreground"
                                  >
                                    {id === "emr" ? (
                                      <SiteIcon
                                        name="fileText"
                                        className={iconography.size.inline}
                                        strokeWidth={iconography.stroke.subtle}
                                      />
                                    ) : id === "bookings" ? (
                                      <SiteIcon
                                        name="calendar"
                                        className={iconography.size.inline}
                                        strokeWidth={iconography.stroke.subtle}
                                      />
                                    ) : id === "transactions" ? (
                                      <ArrowLeftRight
                                        className={iconography.size.inline}
                                        strokeWidth={iconography.stroke.subtle}
                                      />
                                    ) : (
                                      <MoreHorizontal
                                        className={iconography.size.inline}
                                        strokeWidth={iconography.stroke.subtle}
                                      />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="bottom"
                                  className="z-caption"
                                >
                                  {label}
                                </TooltipContent>
                              </Tooltip>
                            ))}
                          </div>
                        </TooltipProvider>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-5 p-6">
                  <Tabs defaultValue="information" className="w-full">
                    <TabsList className="grid h-11 w-full grid-cols-2 rounded-lg bg-muted/60 p-1">
                      <TabsTrigger
                        value="information"
                        className="z-body-md rounded-lg text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                      >
                        Information
                      </TabsTrigger>
                      <TabsTrigger
                        value="uploads"
                        className="z-body-md rounded-lg text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                      >
                        Uploads
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent
                      value="information"
                      className="mt-4 space-y-3.5"
                    >
                      <InfoAccordionSection
                        title="Contact Info"
                        isExpanded={isContactInfoExpanded}
                        onToggle={() =>
                          setIsContactInfoExpanded(!isContactInfoExpanded)
                        }
                        contentClassName="space-y-2.5"
                      >
                        <div className="rounded-lg bg-muted/30 px-3 py-2">
                          <p className="z-eyebrow text-muted-foreground">
                            Email
                          </p>
                          <p className="z-body mt-1 break-words text-foreground">
                            {patient?.email || "Not provided"}
                          </p>
                        </div>
                        <div className="rounded-lg bg-muted/30 px-3 py-2">
                          <p className="z-eyebrow text-muted-foreground">
                            Phone
                          </p>
                          <p className="z-body mt-1 text-foreground">
                            {(supportChat?.contact_info as any)?.phone_number ||
                              patient?.phone_number ||
                              "Not provided"}
                          </p>
                        </div>
                        {(supportChat?.contact_info as any)?.languages &&
                          (supportChat?.contact_info as any).languages.length >
                            0 && (
                            <div className="rounded-lg bg-muted/30 px-3 py-2">
                              <p className="z-eyebrow text-muted-foreground">
                                Languages
                              </p>
                              <p className="z-body mt-1 text-foreground">
                                {(
                                  supportChat?.contact_info as any
                                ).languages.join(", ")}
                              </p>
                            </div>
                          )}
                      </InfoAccordionSection>

                      <InfoAccordionSection
                        title="Last Visits"
                        isExpanded={isLastVisitsExpanded}
                        onToggle={() =>
                          setIsLastVisitsExpanded(!isLastVisitsExpanded)
                        }
                        contentClassName="max-h-80 space-y-2.5 overflow-y-auto"
                      >
                        {supportChat?.last_visits &&
                        supportChat.last_visits.length > 0 ? (
                          supportChat.last_visits.map((visit: any, index) => (
                            <div
                              key={index}
                              className="rounded-lg border border-border bg-muted/20 p-2.5"
                            >
                              <div className="space-y-2.5">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="z-body text-foreground">
                                        {visit.doctor_name}
                                      </span>
                                      <Badge
                                        variant="outline"
                                        className="z-caption rounded-full"
                                      >
                                        {visit.doctor_designation}
                                      </Badge>
                                    </div>
                                    <p className="z-caption mt-1 text-muted-foreground">
                                      {new Date(
                                        visit.appointment_date
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </p>
                                    <p className="z-caption z-numeric mt-1 text-muted-foreground">
                                      Appointment ID:{" "}
                                      {visit.zarity_appointment_id}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                  {visit.prescription_url && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="z-caption h-7 rounded-full px-2.5"
                                      onClick={() => {
                                        const rawName =
                                          visit.prescription_url
                                            .split("/")
                                            .pop() || "Prescription";
                                        const fileName = rawName
                                          .split("?")[0]
                                          .split("#")[0];
                                        const matchedDoc = findDocumentByUrl(
                                          visit.prescription_url
                                        );
                                        handleDocumentViewerOpen(
                                          matchedDoc?.id || "",
                                          visit.prescription_url,
                                          fileName,
                                          matchedDoc?.document_type || "PR",
                                          matchedDoc?.document_date || null,
                                          matchedDoc?.created_at ||
                                            visit.appointment_date,
                                          true
                                        );
                                      }}
                                    >
                                      <FileText className="h-3.5 w-3.5" />
                                      Prescription
                                    </Button>
                                  )}
                                  {visit.meal_plan_url && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="z-caption h-7 rounded-full px-2.5"
                                      onClick={() => {
                                        const rawName =
                                          visit.meal_plan_url
                                            .split("/")
                                            .pop() || "Meal Plan";
                                        const fileName = rawName
                                          .split("?")[0]
                                          .split("#")[0];
                                        const matchedDoc = findDocumentByUrl(
                                          visit.meal_plan_url
                                        );
                                        handleDocumentViewerOpen(
                                          matchedDoc?.id || "",
                                          visit.meal_plan_url,
                                          fileName,
                                          matchedDoc?.document_type || "OT",
                                          matchedDoc?.document_date || null,
                                          matchedDoc?.created_at ||
                                            visit.appointment_date,
                                          true
                                        );
                                      }}
                                    >
                                      <FileText className="h-3.5 w-3.5" />
                                      Meal Plan
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="z-body rounded-lg bg-muted/20 py-6 text-center text-muted-foreground">
                            No recent visits
                          </p>
                        )}
                      </InfoAccordionSection>
                    </TabsContent>
                    <TabsContent value="uploads" className="mt-5 space-y-4">
                      {/* Filter Chips */}
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setSelectedDocumentType(null)}
                            className={cn(
                              "z-caption h-8 rounded-full border px-3 transition-colors",
                              selectedDocumentType === null
                                ? "border-primary bg-primary text-white"
                                : "border-border bg-background text-foreground hover:bg-muted"
                            )}
                          >
                            All
                          </button>
                          {Object.entries(DOCUMENT_TYPES).map(
                            ([type, name]) => (
                              <button
                                key={type}
                                onClick={() =>
                                  handleDocumentTypeSelect(type as DocumentType)
                                }
                                className={cn(
                                  "z-caption h-8 rounded-full border px-3 transition-colors",
                                  selectedDocumentType === type
                                    ? "border-primary bg-primary text-white"
                                    : "border-border bg-background text-foreground hover:bg-muted"
                                )}
                              >
                                {name}
                              </button>
                            )
                          )}
                        </div>

                        {/* Month Navigation */}
                        <div className="flex items-center justify-between">
                          <button
                            onClick={handlePreviousMonth}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background transition-colors hover:bg-muted"
                            disabled={false}
                          >
                            <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                          </button>

                          <span className="z-body-md text-foreground">
                            {getMonthYearString(currentMonth)}
                          </span>

                          <button
                            onClick={handleNextMonth}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={(() => {
                              const now = new Date();
                              const currentMonthEnd = new Date(
                                now.getFullYear(),
                                now.getMonth() + 1,
                                0
                              );
                              const nextMonth = new Date(currentMonth);
                              nextMonth.setMonth(nextMonth.getMonth() + 1);
                              return nextMonth > currentMonthEnd;
                            })()}
                          >
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </div>
                      </div>

                      {/* Documents Grid */}
                      <div className="max-h-96 overflow-y-auto">
                        {documentsLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="flex items-center space-x-2">
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              <span className="z-body text-muted-foreground">
                                Loading documents...
                              </span>
                            </div>
                          </div>
                        ) : patientDocuments && patientDocuments.length > 0 ? (
                          <div className="grid grid-cols-3 gap-3">
                            {patientDocuments.map((document) => {
                              const rawFileName =
                                document.file.split("/").pop() || "Document";
                              const fileName = rawFileName
                                .split("?")[0]
                                .split("#")[0];
                              const isImage = isImageDocument(document.file);
                              const fileExtension =
                                fileName.split(".").pop()?.toLowerCase() || "";
                              const displayName =
                                fileName.length > 15
                                  ? `${fileName.substring(0, 12)}...`
                                  : fileName;

                              return (
                                <div
                                  key={document.id}
                                  className="cursor-pointer rounded-lg border border-border bg-card p-3 transition-colors hover:bg-muted/40"
                                  onClick={() =>
                                    handleDocumentViewerOpen(
                                      document.id,
                                      document.file,
                                      fileName,
                                      document.document_type,
                                      document.document_date,
                                      document.created_at
                                    )
                                  }
                                >
                                  <div className="flex flex-col items-center space-y-2">
                                    {/* Document Thumbnail */}
                                    <div className="flex h-20 w-16 items-center justify-center overflow-hidden rounded border border-border bg-muted">
                                      {isImage ? (
                                        <img
                                          src={document.file}
                                          alt={fileName}
                                          className="w-full h-full object-cover rounded"
                                          onError={(e) => {
                                            // Fallback to icon if image fails to load
                                            const target =
                                              e.target as HTMLImageElement;
                                            target.style.display = "none";
                                            const nextElement =
                                              target.nextElementSibling as HTMLElement;
                                            if (nextElement) {
                                              nextElement.style.display =
                                                "block";
                                            }
                                          }}
                                        />
                                      ) : (
                                        <div className="text-center">
                                          <FileText className="h-6 w-6 text-destructive mx-auto mb-1" />
                                          <div className="z-caption text-muted-foreground">
                                            {fileExtension.toUpperCase()}
                                          </div>
                                        </div>
                                      )}
                                      {/* Fallback for failed images */}
                                      {isImage && (
                                        <div
                                          className="text-center"
                                          style={{ display: "none" }}
                                        >
                                          <Image className="h-6 w-6 text-success mx-auto mb-1" />
                                          <div className="z-caption text-muted-foreground">
                                            {fileExtension.toUpperCase()}
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Document Info */}
                                    <div className="text-center w-full">
                                      <div className="z-caption mb-1 flex items-center justify-center space-x-1 text-muted-foreground">
                                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                        <span
                                          className="truncate"
                                          title={fileName}
                                        >
                                          {displayName}
                                        </span>
                                      </div>
                                      <div className="z-caption text-muted-foreground/80">
                                        {getDocumentTypeName(
                                          document.document_type
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          /* Empty State */
                          <div className="text-center py-8">
                            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                              <FileIcon className="h-6 w-6 text-muted-foreground" />
                            </div>
                            <p className="z-body mb-1 text-muted-foreground">
                              No documents found
                            </p>
                            <p className="z-caption text-muted-foreground/80">
                              {selectedDocumentType
                                ? "Try selecting a different document type or month"
                                : "Documents uploaded for this patient will appear here"}
                            </p>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </aside>
            </>
          );
        })()}

      {/* Add Reading Modal - Only for support chats */}
      {!isInternal && selectedTracker && (
        <AddTrackerEntryModal
          isOpen={showReadingModal}
          onClose={() => {
            setShowReadingModal(false);
            setSelectedTracker(null);
          }}
          metric={selectedTracker}
          onSubmit={() => {}}
          selectedPatient={
            !isInternal && chatDetail
              ? (chatDetail as SupportChatDetail)?.user || null
              : null
          }
          onDataSubmitted={() => {
            // Refresh data if needed
            setShowReadingModal(false);
            setSelectedTracker(null);
          }}
          onValidationResponse={(validation) => {
            toast({
              title:
                validation.title ||
                (validation.category === "error" ? "Error" : "Success"),
              description: validation.description,
              variant:
                validation.category === "error" ? "destructive" : "default",
            });
          }}
          onDataSubmissionError={(errorMessage) => {
            toast({
              title: "Error",
              description: errorMessage,
              variant: "destructive",
            });
          }}
        />
      )}

      {/* File Viewer Dialog (shared primitive) */}
      {selectedDocument && (
        <FilePreviewDialog
          open={isDocumentViewerOpen}
          onOpenChange={(open) => {
            if (!open && (isCategorizeDialogOpen || isDateChangeDialogOpen))
              return;
            setIsDocumentViewerOpen(open);
            if (!open) setViewerCalendarOpen(false);
          }}
          fileUrl={selectedDocument.url}
          fileName={selectedDocument.fileName}
          createdAt={selectedDocument.createdAt}
          headerExtra={
            !selectedDocument.readOnly ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Select
                  value={
                    selectedDocument.documentType === "UC"
                      ? ""
                      : selectedDocument.documentType || ""
                  }
                  onValueChange={(value) =>
                    handleCategorizeTypeSelect(value as DocumentType)
                  }
                >
                  <SelectTrigger className="z-caption h-7 w-[160px] transition-none focus:ring-0 focus:ring-offset-0 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary">
                    <SelectValue placeholder="Uncategorized File" />
                  </SelectTrigger>
                  <SelectContent className="border-border bg-popover text-popover-foreground shadow-lg">
                    {Object.entries(DOCUMENT_TYPES)
                      .filter(([type]) => type !== "UC")
                      .map(([type, name]) => (
                        <SelectItem
                          key={type}
                          value={type}
                          className="focus:bg-accent focus:text-accent-foreground"
                        >
                          {name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                <Popover
                  open={viewerCalendarOpen}
                  onOpenChange={setViewerCalendarOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="z-caption h-7 gap-1.5 px-2 transition-none focus:ring-0 focus:ring-offset-0 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <CalendarIcon className="h-3.5 w-3.5" />
                      {viewerDocumentDate
                        ? format(viewerDocumentDate, "dd MMMM yyyy")
                        : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto border-border bg-popover p-0"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={viewerDocumentDate || undefined}
                      onSelect={(date) => {
                        if (date) handleDateSelect(date);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            ) : undefined
          }
        />
      )}

      {/* Categorization Confirmation Dialog */}
      <AlertDialog open={isCategorizeDialogOpen}>
        <AlertDialogContent
          className="max-w-md"
          onEscapeKeyDown={(e) => {
            if (isCategorizing) e.preventDefault();
            else handleCancelCategorize();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Categorization</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to categorize this document as{" "}
              <strong className="text-foreground">
                {getDocumentTypeName(pendingCategorizeType)}
              </strong>
              ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelCategorize}
              disabled={isCategorizing}
            >
              No
            </Button>
            <Button
              onClick={handleConfirmCategorize}
              disabled={isCategorizing}
              className="bg-primary hover:bg-primary/90"
            >
              {isCategorizing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Categorizing...
                </>
              ) : (
                "Yes"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Date Change Confirmation Dialog */}
      <AlertDialog open={isDateChangeDialogOpen}>
        <AlertDialogContent
          className="max-w-md"
          onEscapeKeyDown={(e) => {
            if (isDateChanging) e.preventDefault();
            else handleCancelDateChange();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Date Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change the document date to{" "}
              <strong className="text-foreground">
                {pendingDate ? format(pendingDate, "dd MMMM yyyy") : ""}
              </strong>
              ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelDateChange}
              disabled={isDateChanging}
            >
              No
            </Button>
            <Button
              onClick={handleConfirmDateChange}
              disabled={isDateChanging}
              className="bg-primary hover:bg-primary/90"
            >
              {isDateChanging ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                "Yes"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
